import { PortalAdapterApi } from "@vacancorp/portal.adapter.api";
import EventBus from "@/event-bus";
import { mapGetters, mapState, mapMutations } from "vuex";
import { getShortDisplayText } from "@/usecase/vacancy";
import portal from "@vacancorp/portal";
import { fetchPlaceCategoryList } from "@/api/adapter.api";
import VueTypedMixin from "vue-typed-mixins";
import PlaceCategoryMixin2 from "@/mixins/place-category2";
import { MapsMixinType, GoogleMapsRef } from "./@types/maps";

const zIndexLargeMarker: number = 99999;

export default VueTypedMixin(PlaceCategoryMixin2).extend({
  name: "mixin-map-icons",
  mixins: [PlaceCategoryMixin2],
  data(): MapsMixinType {
    return {
      v_markers: [],
      moveCurrentLocation: undefined,
      /** @remarks google maps */
      iconVacancy: undefined,
      iconVacancySelected: undefined,
      iconCrowded: undefined,
      iconClosed: undefined,
      iconClosedSelected: undefined,
      iconOpen: undefined,
      iconOpenSelected: undefined,
      iconBusy: undefined,
      iconBusySelected: undefined,
      iconWaiting: undefined,
      iconWaitingSelected: undefined,
      iconEntryRestricted: undefined,
      iconEntryRestrictedSelected: undefined,
      iconDefault: undefined,
      iconDefaultSelected: undefined,
      iconUnknown: undefined,
      iconUnknownSelected: undefined,
      iconNotReady: undefined,
      iconNotReadySelected: undefined,
      iconNotReadyEn: undefined,
      iconNotReadySelectedEn: undefined,
      /** @remarks for disaster prevention map */
      iconNormal: undefined,
      iconNormalSelected: undefined,
      iconCaution: undefined,
      iconCautionSelected: undefined,
      iconWarning: undefined,
      iconWarningSelected: undefined,
      iconCamera: undefined,
      iconCameraSelected: undefined,
    };
  },
  async created() {
    let countryName: string | undefined = this.$store.getters["data/getSelectedCountry"];
    if (countryName === undefined) countryName = "jp";
    const res = await fetchPlaceCategoryList(countryName).catch(() => {
      console.error(`maps view fetchPlaceCategoryList error`);
    });
    this.setGenreList(res);
  },
  computed: {
    ...mapGetters("data", ["getLanguageWithCountryOnly", "getLanguageWithoutCountry"]),
    ...mapState("data", [
      "gMaps",
      "mapsZoom",
      "hasCurrentLocation",
      "location",
      "showGPSDenyAlertByTimeout",
      "selectedPlaceIdHash",
      "selectedGenre",
      "selectedTopPlace",
      "placeList",
    ]),
    ...mapState("ui", ["showDenyGpsAlertView"]),
    // Use $refs with computed for Typescripts
    // because of google lib, it may be undefined if lib is not loaded fully
    googleMapsRef(): GoogleMapsRef {
      return this.$refs.googleMaps as GoogleMapsRef;
    },
    isDisasterPreventionMap(): boolean {
      return this.selectedGenre === "disaster-prevention";
    },
  },
  methods: {
    ...mapMutations("data", ["setMapsZoom", "setGenreList"]),
    // re-assign icon on page switching
    assignCurrentLocationIcon() {
      setTimeout(() => {
        if (this.moveCurrentLocation === undefined) return;
        this.moveCurrentLocation.location = this.location;
        this.googleMapsRef?.moveMapToCurrentLocation(this.moveCurrentLocation);
      }, 100);
    },
    getTargetLocationFromParam(latlng: string): PortalAdapterApi.TopArea {
      const [latStr, lngStr] = latlng.split(",");
      const lat = Number(latStr),
        lng = Number(lngStr);
      const targetLocation: PortalAdapterApi.TopArea = {
        areaName: "unknown",
        displayText: this.$i18n.t("Nav.area") as string,
        location: { lat, lng },
        displayOrder: 1,
      };
      return targetLocation;
    },
    $_setMapZoom(value: number) {
      this.setMapsZoom(value);
      this.gMaps?.setZoom(value);
    },
    resetMapZoom() {
      this.gMaps?.setZoom(this.mapsZoom);
    },
    getMapCenter(): google.maps.LatLng {
      return this.gMaps?.getCenter();
    },
    /** @remarks init markers */
    initMarkerIcons() {
      this.moveCurrentLocation = {
        icon: {
          origin: new google.maps.Point(0, 0),
          scaledSize: new google.maps.Size(64, 64),
          url: require("@/assets/images/current_location.svg"),
        },
        moveToCurrentPlace: true,
        location: this.$store.state.data?.location,
      };
      /** @remarks google maps */
      this.iconVacancy = {
        origin: new google.maps.Point(0, 0),
        scaledSize: new google.maps.Size(44, 54),
        url: require("@/assets/images/map-markers/vacancy.svg"),
      };
      this.iconVacancySelected = {
        origin: new google.maps.Point(0, 0),
        scaledSize: new google.maps.Size(60, 72),
        url: require("@/assets/images/map-markers/vacancy-selected.svg"),
      };
      this.iconCrowded = {
        origin: new google.maps.Point(0, 0),
        scaledSize: new google.maps.Size(44, 54),
        url: require("@/assets/images/map-markers/crowded.svg"),
      };
      this.iconCrowdedSelected = {
        origin: new google.maps.Point(0, 0),
        scaledSize: new google.maps.Size(60, 72),
        url: require("@/assets/images/map-markers/crowded-selected.svg"),
      };
      this.iconClosed = {
        origin: new google.maps.Point(0, 0),
        scaledSize: new google.maps.Size(44, 54),
        url: require("@/assets/images/map-markers/closed.svg"),
      };
      this.iconClosedSelected = {
        origin: new google.maps.Point(0, 0),
        scaledSize: new google.maps.Size(60, 72),
        url: require("@/assets/images/map-markers/closed-selected.svg"),
      };
      this.iconOpen = {
        origin: new google.maps.Point(0, 0),
        scaledSize: new google.maps.Size(44, 54),
        url: require("@/assets/images/map-markers/open.svg"),
      };
      this.iconOpenSelected = {
        origin: new google.maps.Point(0, 0),
        scaledSize: new google.maps.Size(60, 72),
        url: require("@/assets/images/map-markers/open-selected.svg"),
      } as google.maps.Icon;
      this.iconBusy = {
        origin: new google.maps.Point(0, 0),
        scaledSize: new google.maps.Size(44, 54),
        url: require("@/assets/images/map-markers/busy.svg"),
      } as google.maps.Icon;
      this.iconBusySelected = {
        origin: new google.maps.Point(0, 0),
        scaledSize: new google.maps.Size(60, 72),
        url: require("@/assets/images/map-markers/busy-selected.svg"),
      };
      this.iconWaiting = {
        origin: new google.maps.Point(0, 0),
        scaledSize: new google.maps.Size(44, 54),
        labelOrigin: new google.maps.Point(22, 16),
        url: require("@/assets/images/map-markers/waiting/waiting.svg"),
      };
      this.iconWaitingSelected = {
        origin: new google.maps.Point(0, 0),
        scaledSize: new google.maps.Size(60, 72),
        labelOrigin: new google.maps.Point(30, 21),
        url: require("@/assets/images/map-markers/waiting/waiting-selected.svg"),
      };
      this.iconEntryRestricted = {
        origin: new google.maps.Point(0, 0),
        scaledSize: new google.maps.Size(44, 54),
        url: require("@/assets/images/map-markers/entryRestricted.svg"),
      };
      this.iconEntryRestrictedSelected = {
        origin: new google.maps.Point(0, 0),
        scaledSize: new google.maps.Size(60, 72),
        url: require("@/assets/images/map-markers/entryRestricted-selected.svg"),
      };
      this.iconDefault = {
        origin: new google.maps.Point(0, 0),
        scaledSize: new google.maps.Size(44, 53),
        url: require("@/assets/images/map-markers/default.svg"),
      };
      this.iconDefaultSelected = {
        origin: new google.maps.Point(0, 0),
        scaledSize: new google.maps.Size(60, 72),
        url: require("@/assets/images/map-markers/default-selected.svg"),
      };
      this.iconUnknown = {
        origin: new google.maps.Point(0, 0),
        scaledSize: new google.maps.Size(44, 53),
        url: require("@/assets/images/map-markers/unknown.svg"),
      };
      this.iconUnknownSelected = {
        origin: new google.maps.Point(0, 0),
        scaledSize: new google.maps.Size(60, 72),
        url: require("@/assets/images/map-markers/unknown-selected.svg"),
      };
      this.iconNotReady = {
        origin: new google.maps.Point(0, 0),
        scaledSize: new google.maps.Size(44, 53),
        url: require("@/assets/images/map-markers/notReady.svg"),
      };
      this.iconNotReadySelected = {
        origin: new google.maps.Point(0, 0),
        scaledSize: new google.maps.Size(60, 72),
        url: require("@/assets/images/map-markers/notReady-selected.svg"),
      };
      this.iconNotReadyEn = {
        origin: new google.maps.Point(0, 0),
        scaledSize: new google.maps.Size(44, 53),
        url: require("@/assets/images/map-markers/notReady-en.svg"),
      };
      this.iconNotReadySelectedEn = {
        origin: new google.maps.Point(0, 0),
        scaledSize: new google.maps.Size(60, 72),
        url: require("@/assets/images/map-markers/notReady-selected-en.svg"),
      };
      this.iconNormal = {
        origin: new google.maps.Point(0, 0),
        scaledSize: new google.maps.Size(44, 53),
        url: require("@/assets/images/map-markers/activities/normal-jp.svg"),
      };
      this.iconNormalSelected = {
        origin: new google.maps.Point(0, 0),
        scaledSize: new google.maps.Size(60, 72),
        url: require("@/assets/images/map-markers/activities/normal-selected-jp.svg"),
      };
      this.iconCaution = {
        origin: new google.maps.Point(0, 0),
        scaledSize: new google.maps.Size(44, 53),
        url: require("@/assets/images/map-markers/activities/caution-jp.svg"),
      };
      this.iconCautionSelected = {
        origin: new google.maps.Point(0, 0),
        scaledSize: new google.maps.Size(60, 72),
        url: require("@/assets/images/map-markers/activities/caution-selected-jp.svg"),
      };
      this.iconWarning = {
        origin: new google.maps.Point(0, 0),
        scaledSize: new google.maps.Size(44, 53),
        url: require("@/assets/images/map-markers/activities/warning-jp.svg"),
      };
      this.iconWarningSelected = {
        origin: new google.maps.Point(0, 0),
        scaledSize: new google.maps.Size(60, 72),
        url: require("@/assets/images/map-markers/activities/warning-selected-jp.svg"),
      };
      this.iconCamera = {
        origin: new google.maps.Point(0, 0),
        scaledSize: new google.maps.Size(44, 53),
        url: require("@/assets/images/map-markers/activities/camera.svg"),
      };
      this.iconCameraSelected = {
        origin: new google.maps.Point(0, 0),
        scaledSize: new google.maps.Size(60, 72),
        url: require("@/assets/images/map-markers/activities/camera-selected.svg"),
      };
    },
    getMapCenterLocation(): PortalAdapterApi.Location {
      const center: google.maps.LatLng = this.getMapCenter();
      const location: PortalAdapterApi.Location = {
        lat: center.lat(),
        lng: center.lng(),
      };
      return location;
    },
    selectPlace(placeIdHash: string): void {
      // GA, GTM tracking
      this.$gtag.event("click", {
        event_category: "Pin",
        event_label: placeIdHash,
        value: 1,
      });

      this.$store.commit("data/setSelectedPlaceIdHash", placeIdHash);
      EventBus.$emit("toggle-pin-selected-status", "byMarker");
      EventBus.$emit("search-again", false);
      EventBus.$emit("gMap-stop-drag", false);
      EventBus.$emit("change-gMap-in-action", false);
      EventBus.$emit("move-maps-card-to-selected-marker");
    },
    /**
     * @remarks reset markers when clicking on another marker
     */
    // find clicked marker on PC
    findSpecifiedMarkerByPlaceIdHash(placeIdHash: string): google.maps.Marker | undefined {
      const selectedMarker: google.maps.Marker[] | undefined = this.v_markers.filter((marker: google.maps.Marker) => {
        const cursorString: string | null | undefined = marker.getCursor();
        if (cursorString === null || cursorString === undefined) return;
        const markerPlaceIdHash: string = cursorString.split("||")[0];
        return markerPlaceIdHash === placeIdHash;
      });
      return selectedMarker !== undefined ? selectedMarker[0] : undefined;
    },
    // find selected marker on mobile
    findSelectedMarker(): google.maps.Marker | undefined {
      const selectedMarker = this.v_markers.filter((marker: google.maps.Marker) => {
        const cursorString: string | null | undefined = marker.getCursor();
        if (cursorString === null || cursorString === undefined) return;
        const markerPlaceIdHash: string = cursorString.split("||")[0];
        return markerPlaceIdHash === this.selectedPlaceIdHash;
      }) as google.maps.Marker[] | undefined;
      return selectedMarker !== undefined ? selectedMarker[0] : undefined;
    },
    resetMarkerIcons() {
      const marker: google.maps.Marker | undefined = this.v_markers.find(
        (marker: google.maps.Marker) => marker.getZIndex() === zIndexLargeMarker,
      );

      if (marker === undefined) return;
      if (this.iconDefault !== undefined) {
        marker.setIcon(this.iconDefault);
      }
      // reset all to small style
      const icon = marker.getIcon() as google.maps.ReadonlyIcon | null;
      if (icon === null) return;

      // small markers are clickable
      marker.setClickable(true);

      // reset label size
      const cursorString: string | null | undefined = marker.getCursor();
      if (cursorString === undefined || cursorString === null) return;

      if (cursorString.indexOf("||") !== -1) {
        const parsedCursor = cursorString.split("||");
        const vacancyStatus = parsedCursor[1];
        const vacancyText = parsedCursor[2];
        const waitingType: string | "undefined" = parsedCursor[3];
        const savedZIndex: string | "undefined" = parsedCursor[4];
        const isCameraPin: boolean = parsedCursor[5] === "true";
        // reset zIndex
        marker.setZIndex(Number(savedZIndex));

        // reset all to small icons
        if (isCameraPin) {
          if (this.iconCamera === undefined) return;
          marker.setIcon(this.iconCamera);
          marker.setLabel("");
        } else if (vacancyStatus === "vacancy") {
          if (this.isDisasterPreventionMap) {
            if (this.iconNormal === undefined) return;
            this.iconNormal.url = require(`@/assets/images/map-markers/activities/normal-${this.getLanguageWithCountryOnly}.svg`);
            marker.setIcon(this.iconNormal);
            marker.setLabel("");
          } else {
            if (this.iconVacancy === undefined) return;
            marker.setIcon(this.iconVacancy);
            marker.setLabel("");
          }
        } else if (vacancyStatus === "open") {
          if (this.iconOpen === undefined) return;
          marker.setIcon(this.iconOpen);
          marker.setLabel("");
        } else if (vacancyStatus === "crowded") {
          if (this.isDisasterPreventionMap) {
            if (this.iconCaution === undefined) return;
            this.iconCaution.url = require(`@/assets/images/map-markers/activities/caution-${this.getLanguageWithCountryOnly}.svg`);
            marker.setIcon(this.iconCaution);
            marker.setLabel("");
          } else {
            if (this.iconCrowded === undefined) return;
            marker.setIcon(this.iconCrowded);
            marker.setLabel("");
          }
        } else if (vacancyStatus === "busy" || vacancyStatus === "full") {
          if (this.isDisasterPreventionMap) {
            if (this.iconWarning === undefined) return;
            this.iconWarning.url = require(`@/assets/images/map-markers/activities/warning-${this.getLanguageWithCountryOnly}.svg`);
            marker.setIcon(this.iconWarning);
            marker.setLabel("");
          } else {
            if (this.iconBusy === undefined) return;
            marker.setIcon(this.iconBusy);
            marker.setLabel("");
          }
        } else if (vacancyStatus === "waiting") {
          if (this.iconWaiting === undefined) return;
          const country = this.getLanguageWithCountryOnly;
          if (waitingType === "waitingMinutes") {
            this.iconWaiting.url = require(`@/assets/images/map-markers/waiting/min/normal-${country}.svg`);
          } else {
            this.iconWaiting.url = require(`@/assets/images/map-markers/waiting/inline/normal-${country}.svg`);
          }
          marker.setIcon(this.iconWaiting);
          const displayText: string = getShortDisplayText(vacancyStatus, vacancyText);
          // marker.setLabel(displayText);
          marker.setLabel({
            color: "white",
            fontSize: "18px",
            fontWeight: "700",
            text: displayText,
            fontFamily: "Noto Sans CJK JP",
          });
        } else if (
          vacancyStatus === "closed" ||
          vacancyStatus === "lo" ||
          vacancyStatus === "maintenance" ||
          vacancyStatus === "disabled"
        ) {
          if (this.iconClosed === undefined) return;
          marker.setIcon(this.iconClosed);
          marker.setLabel("");
        } else if (vacancyStatus === "entryRestricted") {
          if (this.iconEntryRestricted === undefined) return;
          marker.setIcon(this.iconEntryRestricted);
          marker.setLabel("");
        } else if (vacancyStatus === "unknown") {
          if (this.iconUnknown === undefined) return;
          marker.setIcon(this.iconUnknown);
          marker.setLabel("");
        } else if (vacancyStatus === "notReady") {
          if (this.getLanguageWithoutCountry === "en") {
            if (this.iconNotReadyEn === undefined) return;
            marker.setIcon(this.iconNotReadyEn);
            marker.setLabel("");
          } else {
            if (this.iconNotReady === undefined) return;
            marker.setIcon(this.iconNotReady);
            marker.setLabel("");
          }
        } else {
          if (this.iconDefault === undefined) return;
          marker.setIcon(this.iconDefault);
          marker.setLabel("");
        }
      } else {
        // cursorString.indexOf("||") === -1
        // for place without vacancy data
        if (this.iconUnknown === undefined) return;
        marker.setIcon(this.iconUnknown);
        marker.setLabel("");
        marker.setZIndex(100); // same as closed
      }
    },
    enlargeSelectedMarker(marker: google.maps.Marker) {
      if (marker === undefined) return;
      const selectedMarkerIcon = marker.getIcon() as google.maps.ReadonlyIcon;
      if (selectedMarkerIcon && selectedMarkerIcon !== null) {
        // to mark selected and enlarged one non-clickable
        marker.setClickable(false);
        // set layer
        marker.setZIndex(zIndexLargeMarker);
        // style label
        const cursorString: string | null | undefined = marker.getCursor();
        if (cursorString === undefined || cursorString === null) return;
        const parsedCursor = cursorString.split("||");
        const vacancyStatus = parsedCursor[1];
        const vacancyText = parsedCursor[2];
        const waitingType: string | "undefined" = parsedCursor[3];
        const isCameraPin: boolean = parsedCursor[5] === "true";
        if (isCameraPin) {
          if (this.iconCameraSelected === undefined) return;
          marker.setIcon(this.iconCameraSelected);
          marker.setLabel("");
        } else if (vacancyStatus === "vacancy") {
          if (this.isDisasterPreventionMap) {
            if (this.iconNormalSelected === undefined) return;
            this.iconNormalSelected.url = require(`@/assets/images/map-markers/activities/normal-selected-${this.getLanguageWithCountryOnly}.svg`);
            marker.setIcon(this.iconNormalSelected);
            marker.setLabel("");
          } else {
            if (this.iconVacancySelected === undefined) return;
            marker.setIcon(this.iconVacancySelected);
            marker.setLabel("");
          }
        } else if (vacancyStatus === "open") {
          if (this.iconOpenSelected === undefined) return;
          marker.setIcon(this.iconOpenSelected);
          marker.setLabel("");
        } else if (vacancyStatus === "crowded") {
          if (this.isDisasterPreventionMap) {
            if (this.iconCautionSelected === undefined) return;
            this.iconCautionSelected.url = require(`@/assets/images/map-markers/activities/caution-selected-${this.getLanguageWithCountryOnly}.svg`);
            marker.setIcon(this.iconCautionSelected);
            marker.setLabel("");
          } else {
            if (this.iconCrowdedSelected === undefined) return;
            marker.setIcon(this.iconCrowdedSelected);
            marker.setLabel("");
          }
        } else if (vacancyStatus === "waiting") {
          if (this.iconWaitingSelected === undefined) return;
          const country = this.getLanguageWithCountryOnly;
          if (waitingType === "waitingMinutes") {
            this.iconWaitingSelected.url = require(`@/assets/images/map-markers/waiting/min/selected-${country}.svg`);
          } else {
            this.iconWaitingSelected.url = require(`@/assets/images/map-markers/waiting/inline/selected-${country}.svg`);
          }
          marker.setIcon(this.iconWaitingSelected);
          const displayText: string = getShortDisplayText(vacancyStatus, vacancyText);
          // marker.setLabel(displayText);
          marker.setLabel({
            color: "white",
            fontSize: "22px",
            fontWeight: "700",
            text: displayText,
            fontFamily: "Noto Sans CJK JP",
          });
        } else if (vacancyStatus === "busy" || vacancyStatus === "full") {
          if (this.isDisasterPreventionMap) {
            if (this.iconWarningSelected === undefined) return;
            this.iconWarningSelected.url = require(`@/assets/images/map-markers/activities/warning-selected-${this.getLanguageWithCountryOnly}.svg`);
            marker.setIcon(this.iconWarningSelected);
            marker.setLabel("");
          } else {
            if (this.iconBusySelected === undefined) return;
            marker.setIcon(this.iconBusySelected);
            marker.setLabel("");
          }
        } else if (
          vacancyStatus === "closed" ||
          vacancyStatus === "lo" ||
          vacancyStatus === "maintenance" ||
          vacancyStatus === "disabled"
        ) {
          if (this.iconClosedSelected === undefined) return;
          marker.setIcon(this.iconClosedSelected);
          marker.setLabel("");
        } else if (vacancyStatus === "entryRestricted") {
          if (this.iconEntryRestrictedSelected === undefined) return;
          marker.setIcon(this.iconEntryRestrictedSelected);
          marker.setLabel("");
        } else if (vacancyStatus === "notReady") {
          if (this.getLanguageWithoutCountry === "en") {
            if (this.iconNotReadySelectedEn === undefined) return;
            marker.setIcon(this.iconNotReadySelectedEn);
            marker.setLabel("");
          } else {
            if (this.iconNotReadySelected === undefined) return;
            marker.setIcon(this.iconNotReadySelected);
            marker.setLabel("");
          }
        } else {
          if (this.iconUnknownSelected === undefined) return;
          marker.setIcon(this.iconUnknownSelected);
          marker.setLabel("");
        }
      } else {
        // selectedMarkerIcon undefined
        if (this.iconDefaultSelected === undefined) return;
        marker.setIcon(this.iconDefaultSelected);
        marker.setLabel("");
      }
    },
    styleMarkers(vacancyList: PortalAdapterApi.ResponseGetMergedDisplayVacancy[]) {
      this.v_markers.forEach((marker: google.maps.Marker, index: number) => {
        const cursorString: string | null | undefined = marker.getCursor();
        if (cursorString === null || cursorString === undefined) return;
        const placeIdHash: string = cursorString.indexOf("||") > -1 ? cursorString.split("||")[0] : cursorString;
        const vacancy: PortalAdapterApi.ResponseGetMergedDisplayVacancy | undefined = vacancyList.find(
          (vacancy: PortalAdapterApi.ResponseGetMergedDisplayVacancy) => vacancy.placeIdHash === placeIdHash,
        );
        // cannot find vacancy data with the same placeIdHash
        // it should be an error
        if (vacancy === undefined) {
          if (marker.getClickable()) {
            if (this.iconUnknown === undefined) return;
            marker.setIcon(this.iconUnknown);
            marker.setLabel("");
          } else {
            if (this.iconUnknownSelected === undefined) return;
            marker.setIcon(this.iconUnknownSelected);
            marker.setLabel("");
          }
          return;
        }

        let cursorSplit: string[] = [];

        /**
         * @remarks
         * classify zIndex to groups by statusOrder in positive number
         * cannot use index of sortedPlaceList cause we need the latest vacancy
         * to switch pins in realtime
         * ** when markers show with label (status: waiting), if zIndex are the same
         *    label of markers are showing up in a overlap, so needs to count with index
         */
        const displayOrder: number = vacancy.statusOrder;
        const zIndexFromDisplayOrder = zIndexLargeMarker - displayOrder - index;

        if (marker.getClickable()) {
          marker.setZIndex(zIndexFromDisplayOrder);
        } else {
          marker.setZIndex(zIndexLargeMarker);
        }

        const markerPlace: PortalAdapterApi.ResponseGetPublicPlace | undefined = this.placeList.find(
          (place: PortalAdapterApi.ResponseGetPublicPlace) => place.placeIdHash === placeIdHash,
        );
        const vacancyStatus: string = vacancy.status;
        const vacancyText: string = vacancy.displayVacancyText;
        const waitingType: string | undefined =
          vacancy.status === "waiting" ? `${vacancy?.waitingDetail?.waitingType}` : "undefined";
        const isCameraPin: boolean =
          markerPlace?.genre.type === "disaster-prevention" &&
          !!markerPlace?.categoryTypeList.find((categoryType) => categoryType.type === "live-camera");
        cursorSplit.push(placeIdHash);
        cursorSplit.push(vacancyStatus);
        cursorSplit.push(vacancyText);
        cursorSplit.push(waitingType);
        cursorSplit.push(String(zIndexFromDisplayOrder));
        cursorSplit.push(String(isCameraPin));
        marker.setCursor(cursorSplit.join("||"));

        if (isCameraPin) {
          if (marker.getClickable()) {
            if (this.iconCamera === undefined) return;
            marker.setIcon(this.iconCamera);
            marker.setLabel("");
          } else {
            if (this.iconCameraSelected === undefined) return;
            marker.setIcon(this.iconCameraSelected);
            marker.setLabel("");
          }
        } else if (vacancy.status === "vacancy") {
          if (this.isDisasterPreventionMap) {
            if (marker.getClickable()) {
              if (this.iconNormal === undefined) return;
              this.iconNormal.url = require(`@/assets/images/map-markers/activities/normal-${this.getLanguageWithCountryOnly}.svg`);
              marker.setIcon(this.iconNormal);
              marker.setLabel("");
            } else {
              if (this.iconNormalSelected === undefined) return;
              this.iconNormalSelected.url = require(`@/assets/images/map-markers/activities/normal-selected-${this.getLanguageWithCountryOnly}.svg`);
              marker.setIcon(this.iconNormalSelected);
              marker.setLabel("");
            }
          } else {
            if (marker.getClickable()) {
              if (this.iconVacancy === undefined) return;
              marker.setIcon(this.iconVacancy);
              marker.setLabel("");
            } else {
              if (this.iconVacancySelected === undefined) return;
              marker.setIcon(this.iconVacancySelected);
              marker.setLabel("");
            }
          }
        } else if (vacancy.status === "open") {
          if (marker.getClickable()) {
            if (this.iconOpen === undefined) return;
            marker.setIcon(this.iconOpen);
            marker.setLabel("");
          } else {
            if (this.iconOpenSelected === undefined) return;
            marker.setIcon(this.iconOpenSelected);
            marker.setLabel("");
          }
        } else if (vacancy.status === "crowded") {
          if (this.isDisasterPreventionMap) {
            if (marker.getClickable()) {
              if (this.iconCaution === undefined) return;
              marker.setIcon(this.iconCaution);
              this.iconCaution.url = require(`@/assets/images/map-markers/activities/caution-${this.getLanguageWithCountryOnly}.svg`);
              marker.setLabel("");
            } else {
              if (this.iconCautionSelected === undefined) return;
              this.iconCautionSelected.url = require(`@/assets/images/map-markers/activities/caution-selected-${this.getLanguageWithCountryOnly}.svg`);
              marker.setIcon(this.iconCautionSelected);
              marker.setLabel("");
            }
          } else {
            if (marker.getClickable()) {
              if (this.iconCrowded === undefined) return;
              marker.setIcon(this.iconCrowded);
              marker.setLabel("");
            } else {
              if (this.iconCrowdedSelected === undefined) return;
              marker.setIcon(this.iconCrowdedSelected);
              marker.setLabel("");
            }
          }
        } else if (vacancy.status === "busy" || vacancy.status === "full") {
          if (this.isDisasterPreventionMap) {
            if (marker.getClickable()) {
              if (this.iconWarning === undefined) return;
              this.iconWarning.url = require(`@/assets/images/map-markers/activities/warning-${this.getLanguageWithCountryOnly}.svg`);
              marker.setIcon(this.iconWarning);
              marker.setLabel("");
            } else {
              if (this.iconWarningSelected === undefined) return;
              this.iconWarningSelected.url = require(`@/assets/images/map-markers/activities/warning-selected-${this.getLanguageWithCountryOnly}.svg`);
              marker.setIcon(this.iconWarningSelected);
              marker.setLabel("");
            }
          } else {
            if (marker.getClickable()) {
              if (this.iconBusy === undefined) return;
              marker.setIcon(this.iconBusy);
              marker.setLabel("");
            } else {
              if (this.iconBusySelected === undefined) return;
              marker.setIcon(this.iconBusySelected);
              marker.setLabel("");
            }
          }
        } else if (vacancy.status === "waiting") {
          if (marker.getClickable()) {
            if (this.iconWaiting === undefined) return;
            const country = this.getLanguageWithCountryOnly;
            if (waitingType === "waitingMinutes") {
              this.iconWaiting.url = require(`@/assets/images/map-markers/waiting/min/normal-${country}.svg`);
            } else {
              this.iconWaiting.url = require(`@/assets/images/map-markers/waiting/inline/normal-${country}.svg`);
            }
            marker.setIcon(this.iconWaiting);
            const displayText: string = getShortDisplayText(vacancyStatus, vacancyText);
            //marker.setLabel(displayText);
            marker.setLabel({
              color: "white",
              fontSize: "16px",
              fontWeight: "700",
              text: displayText,
              fontFamily: "Noto Sans CJK JP",
            });
          } else {
            if (this.iconWaitingSelected === undefined) return;
            const country = this.getLanguageWithCountryOnly;
            if (waitingType === "waitingMinutes") {
              this.iconWaitingSelected.url = require(`@/assets/images/map-markers/waiting/min/selected-${country}.svg`);
            } else {
              this.iconWaitingSelected.url = require(`@/assets/images/map-markers/waiting/inline/selected-${country}.svg`);
            }
            marker.setIcon(this.iconWaitingSelected);
            const displayText: string = getShortDisplayText(vacancyStatus, vacancyText);
            // marker.setLabel(displayText);
            marker.setLabel({
              color: "white",
              fontSize: "22px",
              fontWeight: "700",
              text: displayText,
              fontFamily: "Noto Sans CJK JP",
            });
          }
        } else if (
          vacancy.status === "closed" ||
          vacancy.status === "lo" ||
          vacancy.status === "maintenance" ||
          vacancy.status === "disabled"
        ) {
          if (marker.getClickable()) {
            if (this.iconClosed === undefined) return;
            marker.setIcon(this.iconClosed);
            marker.setLabel("");
          } else {
            if (this.iconClosedSelected === undefined) return;
            marker.setIcon(this.iconClosedSelected);
            marker.setLabel("");
          }
        } else if (vacancy.status === "entryRestricted") {
          if (marker.getClickable()) {
            if (this.iconEntryRestricted === undefined) return;
            marker.setIcon(this.iconEntryRestricted);
            marker.setLabel("");
          } else {
            if (this.iconEntryRestrictedSelected === undefined) return;
            marker.setIcon(this.iconEntryRestrictedSelected);
            marker.setLabel("");
          }
        } else if (vacancy.status === "notReady") {
          if (this.getLanguageWithoutCountry === "en") {
            if (marker.getClickable()) {
              if (this.iconNotReadyEn === undefined) return;
              marker.setIcon(this.iconNotReadyEn);
              marker.setLabel("");
            } else {
              if (this.iconNotReadySelectedEn === undefined) return;
              marker.setIcon(this.iconNotReadySelectedEn);
              marker.setLabel("");
            }
          } else {
            if (marker.getClickable()) {
              if (this.iconNotReady === undefined) return;
              marker.setIcon(this.iconNotReady);
              marker.setLabel("");
            } else {
              if (this.iconNotReadySelected === undefined) return;
              marker.setIcon(this.iconNotReadySelected);
              marker.setLabel("");
            }
          }
        } else {
          if (marker.getClickable()) {
            if (this.iconUnknown === undefined) return;
            marker.setIcon(this.iconUnknown);
            marker.setLabel("");
          } else {
            if (this.iconUnknownSelected === undefined) return;
            marker.setIcon(this.iconUnknownSelected);
            marker.setLabel("");
          }
        }
      });
    },
    addMarkers(sortedPlaceList: portal.PublicPlaceWithVacancyId[]) {
      if (this.v_markers.length > 0) this.clearMarkers();
      sortedPlaceList.forEach((placeItem: portal.PublicPlaceWithVacancyId) => {
        if (placeItem !== undefined) {
          // create marker
          const marker = new google.maps.Marker({
            position: {
              lat: placeItem.location.lat,
              lng: placeItem.location.lng,
            },
            map: this.gMaps,
            icon: this.iconDefault,
            cursor: "" + placeItem.placeIdHash + "",
          });
          // Add click listener for markers
          // need to use annonumous function in for-loop
          ((_, pItem: portal.PublicPlaceWithVacancyId) => {
            //param
            marker.addListener("click", () => {
              this.selectPlace(pItem.placeIdHash);
            });
          })(marker, placeItem); //value
          this.v_markers.push(marker);
        }
      }); // end of for
    },
    clearMarkers() {
      for (let i: number = 0; i < this.v_markers.length; i++) {
        this.v_markers[i].setMap(null);
      }
      this.v_markers = [];
    },
    findIndexToMovePlacePin(placeIdHash: string, sortedPlaceList: portal.PublicPlaceWithVacancyId[]): number {
      const toIndex: number = sortedPlaceList.findIndex((place: portal.PublicPlaceWithVacancyId) => {
        return place.placeIdHash === placeIdHash;
      });
      return toIndex;
    },
    enlargeMarkerUI() {
      this.resetMarkerIcons();
      const marker = this.findSelectedMarker() as google.maps.Marker | undefined;
      if (marker === undefined) return;
      this.enlargeSelectedMarker(marker);
    },
  },
});
